const STORAGE_KEY = 'theme'

/** Shared dark mode state — single source of truth for the whole app */
const isDark = ref(false)

/**
 * When true, dark mode is suppressed regardless of user preference.
 * Used by the print layout so print preview and print-to-PDF are always
 * rendered in light mode.
 */
const _forcedLight = ref(false)

function applyDark(val: boolean) {
  if (val) {
    document.documentElement.classList.add('dark')
  } else {
    document.documentElement.classList.remove('dark')
  }
  localStorage.setItem(STORAGE_KEY, val ? 'dark' : 'light')
}

/** Call once on app startup to restore preference from localStorage */
export function initDarkMode() {
  const stored = localStorage.getItem(STORAGE_KEY)
  const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
  isDark.value = stored ? stored === 'dark' : prefersDark
  // Apply without writing back to localStorage (already stored or system default)
  if (isDark.value) {
    document.documentElement.classList.add('dark')
  }
}

/**
 * Force light mode without changing the stored user preference.
 * Pass `true` to suppress dark mode, `false` to restore it.
 */
export function overrideLightMode(force: boolean) {
  _forcedLight.value = force
  if (force) {
    document.documentElement.classList.remove('dark')
  } else if (isDark.value) {
    document.documentElement.classList.add('dark')
  }
}

export function useDarkMode() {
  const effectiveIsDark = computed(() => _forcedLight.value ? false : isDark.value)

  function toggle(val?: boolean) {
    isDark.value = val !== undefined ? val : !isDark.value
    if (!_forcedLight.value) {
      applyDark(isDark.value)
    } else {
      localStorage.setItem(STORAGE_KEY, isDark.value ? 'dark' : 'light')
    }
  }

  return { isDark: effectiveIsDark as Readonly<Ref<boolean>>, toggle }
}
